import { ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET, ONBOARDING_AGENT_TRIGGER_SIZE } from "../constants";
import type {
    OnboardingAgentAxisAnchor,
    OnboardingAgentTriggerAnchor,
    OnboardingAgentViewportPosition,
} from "../OnboardingAgent.types";

import { clampPositionToViewport, getViewportSize } from "./OnboardingAgentPosition.helpers";

/**
 * Fraction of the available track within which the trigger counts as "docked" to an edge. Inside
 * this zone the exact pixel distance from that edge is kept, so a corner-parked trigger stays in the
 * corner; outside of it the trigger keeps its relative position instead.
 */
const EDGE_DOCK_RATIO = 0.25;

/**
 * Describes where the trigger sits relative to the viewport, captured once when the user drops it.
 * Recomputing pixels from this anchor on every resize (instead of reflowing the previous pixels)
 * keeps the trigger reversible: shrinking and restoring the window lands it back on the same spot.
 */
const createAxisAnchor = (coordinate: number, elementSize: number, viewportSize: number): OnboardingAgentAxisAnchor => {
    const available = viewportSize - elementSize;

    if (available <= 0) {
        return { kind: "edge", edge: "start", offset: coordinate };
    }

    const distanceFromStartEdge = coordinate;
    const distanceFromEndEdge = available - coordinate;
    const dockThreshold = available * EDGE_DOCK_RATIO;

    if (distanceFromStartEdge <= distanceFromEndEdge && distanceFromStartEdge <= dockThreshold) {
        return { kind: "edge", edge: "start", offset: distanceFromStartEdge };
    }

    if (distanceFromEndEdge < distanceFromStartEdge && distanceFromEndEdge <= dockThreshold) {
        return { kind: "edge", edge: "end", offset: distanceFromEndEdge };
    }

    return { kind: "ratio", ratio: (coordinate + elementSize / 2) / viewportSize };
};

const resolveAxisAnchor = (anchor: OnboardingAgentAxisAnchor, elementSize: number, viewportSize: number): number => {
    if (anchor.kind === "edge") {
        return anchor.edge === "start" ? anchor.offset : viewportSize - elementSize - anchor.offset;
    }

    return anchor.ratio * viewportSize - elementSize / 2;
};

export const createDefaultTriggerAnchor = (): OnboardingAgentTriggerAnchor => ({
    horizontal: { kind: "edge", edge: "end", offset: ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET },
    vertical: { kind: "edge", edge: "end", offset: ONBOARDING_AGENT_DEFAULT_SCREEN_OFFSET },
});

export const createTriggerAnchorFromPosition = (
    position: OnboardingAgentViewportPosition,
): OnboardingAgentTriggerAnchor => {
    const viewport = getViewportSize();

    return {
        horizontal: createAxisAnchor(position.x, ONBOARDING_AGENT_TRIGGER_SIZE, viewport.width),
        vertical: createAxisAnchor(position.y, ONBOARDING_AGENT_TRIGGER_SIZE, viewport.height),
    };
};

export const resolveTriggerAnchorToPosition = (
    anchor: OnboardingAgentTriggerAnchor,
): OnboardingAgentViewportPosition => {
    const viewport = getViewportSize();

    return clampPositionToViewport(
        {
            x: resolveAxisAnchor(anchor.horizontal, ONBOARDING_AGENT_TRIGGER_SIZE, viewport.width),
            y: resolveAxisAnchor(anchor.vertical, ONBOARDING_AGENT_TRIGGER_SIZE, viewport.height),
        },
        ONBOARDING_AGENT_TRIGGER_SIZE,
        ONBOARDING_AGENT_TRIGGER_SIZE,
    );
};
